import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const ACCESS_KEY = "fuelops_access_token";
export const REFRESH_KEY = "fuelops_refresh_token";

const client = axios.create({ baseURL: BACKEND_URL });

let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

client.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err.response?.status === 401 && onUnauthorized) {
      onUnauthorized();
    }
    return Promise.reject(err);
  }
);

export const api = {
  // Auth
  register: (body) => client.post("/api/auth/register", body).then((r) => r.data),
  login: (body) => client.post("/api/auth/login", body).then((r) => r.data),
  me: () => client.get("/api/auth/me").then((r) => r.data),
  logout: () => client.post("/api/auth/logout").then((r) => r.data),

  // stations
  listStations: () => client.get("/api/stations").then((r) => r.data),
  createStation: (body) => client.post("/api/stations", body).then((r) => r.data),
  updateStation: (id, body) => client.put(`/api/stations/${id}`, body).then((r) => r.data),
  deleteStation: (id) => client.delete(`/api/stations/${id}`).then((r) => r.data),

  // tanks
  listTanks: (stationId) => client.get("/api/tanks", { params: { station_id: stationId } }).then((r) => r.data),
  updateTank: (id, body) => client.put(`/api/tanks/${id}`, body).then((r) => r.data),

  // deliveries
  listDeliveries: (stationId) => client.get("/api/deliveries", { params: { station_id: stationId } }).then((r) => r.data),
  createDelivery: (body) => client.post("/api/deliveries", body).then((r) => r.data),
  deleteDelivery: (id) => client.delete(`/api/deliveries/${id}`).then((r) => r.data),

  // shifts
  listShifts: (stationId) => client.get("/api/shifts", { params: { station_id: stationId } }).then((r) => r.data),
  createShift: (body) => client.post("/api/shifts", body).then((r) => r.data),
  deleteShift: (id) => client.delete(`/api/shifts/${id}`).then((r) => r.data),

  // checklist
  listChecklistTemplates: (stationId) =>
    client.get("/api/checklist/templates", { params: { station_id: stationId } }).then((r) => r.data),
  updateChecklistTemplate: (id, body) =>
    client.put(`/api/checklist/templates/${id}`, body).then((r) => r.data),
  listChecklistCompletions: (stationId) =>
    client.get("/api/checklist/completions", { params: { station_id: stationId } }).then((r) => r.data),
  completeChecklist: (body) => client.post("/api/checklist/complete", body).then((r) => r.data),
  uncheckChecklist: (id) => client.delete(`/api/checklist/complete/${id}`).then((r) => r.data),

  // equipment
  listEquipment: (stationId) => client.get("/api/equipment", { params: { station_id: stationId } }).then((r) => r.data),
  createEquipment: (body) => client.post("/api/equipment", body).then((r) => r.data),
  updateEquipment: (id, body) => client.put(`/api/equipment/${id}`, body).then((r) => r.data),
  deleteEquipment: (id) => client.delete(`/api/equipment/${id}`).then((r) => r.data),

  // maintenance
  listMaintenance: (stationId, equipmentId) =>
    client
      .get("/api/maintenance", { params: { station_id: stationId, ...(equipmentId ? { equipment_id: equipmentId } : {}) } })
      .then((r) => r.data),
  createMaintenance: (body) => client.post("/api/maintenance", body).then((r) => r.data),

  // P&L
  plSummary: (stationId, days = 7) =>
    client.get("/api/pl/summary", { params: { station_id: stationId, days } }).then((r) => r.data),

  // Reminders
  listUpcomingReminders: (stationId) =>
    client.get("/api/reminders/upcoming", { params: { station_id: stationId } }).then((r) => r.data),
  runReminders: (stationId) =>
    client.post("/api/reminders/run", null, { params: { station_id: stationId } }).then((r) => r.data),
  schedulePreview: (stationId) =>
    client.get("/api/reminders/schedule/preview", { params: { station_id: stationId } }).then((r) => r.data),
  listTimezones: () => client.get("/api/timezones").then((r) => r.data),

  // CSV export (Bearer token can't be attached to <a> href, so we fetch and open blob URL)
  exportCsv: async (kind, stationId) => {
    const res = await client.get(`/api/export/${kind}.csv`, {
      params: { station_id: stationId },
      responseType: "blob",
    });
    return res.data;
  },

  // seed
  seed: () => client.post("/api/seed").then((r) => r.data),
};

export const FUEL_LABELS = { regular: "Regular 87", mid: "Mid-Grade 89", premium: "Premium 93" };
export const FUEL_COLORS = { regular: "#10B981", mid: "#F59E0B", premium: "#FF6B00" };

export const todayKey = () => new Date().toISOString().slice(0, 10);

export function periodKeyFor(frequency, dateObj = new Date()) {
  const d = new Date(dateObj);
  if (frequency === "daily") return d.toISOString().slice(0, 10);
  if (frequency === "monthly") return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
  if (frequency === "weekly") {
    const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const dayNum = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
    return `${tmp.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
  }
  return d.toISOString().slice(0, 10);
}

export const currency = (n) =>
  (n ?? 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

export const numFmt = (n, d = 0) =>
  (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
